/**
 * @file useWaiterOrders.ts
 * @description Real-time hook synchronizing active waiter orders with 3-pillar status.
 * Department filtering: if the authenticated waiter has a department assigned
 * (via venue_members.department_id), the order feed is restricted to orders
 * from that department only. Unassigned waiters see all orders.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../config/supabase.config';
import {
  fetchActiveWaiterOrders,
  confirmOrderToKitchen,
  serveOrderToTable,
  collectOrderPayment,
  resolveGuestCall,
  cancelOrder,
} from '../services/waiterOrders.service';
import type { WaiterActiveOrder } from '../../../types/waiterOrder.types';


// Audio alerts for floor staff (cached singletons to avoid GC churn)
const READY_PICKUP_AUDIO = 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3';
const GUEST_CALL_AUDIO = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

let readyAudioInstance: HTMLAudioElement | null = null;
let guestCallAudioInstance: HTMLAudioElement | null = null;

const playReadySound = (): void => {
  try {
    if (typeof window === 'undefined') return;
    if (!readyAudioInstance) readyAudioInstance = new Audio(READY_PICKUP_AUDIO);
    readyAudioInstance.currentTime = 0;
    readyAudioInstance.play().catch(() => {});
  } catch {}
};

const playCallSound = (): void => {
  try {
    if (typeof window === 'undefined') return;
    if (!guestCallAudioInstance) guestCallAudioInstance = new Audio(GUEST_CALL_AUDIO);
    guestCallAudioInstance.currentTime = 0;
    guestCallAudioInstance.play().catch(() => {});
  } catch {}
};

/**
 * Checks whether an order belongs to the given department.
 * Matches against metadata.department_id, metadata.department_name, or metadata.department.
 * Returns true when no department is specified (show-all fallback).
 */
const orderMatchesDepartment = (
  order: WaiterActiveOrder,
  departmentId: string | null,
  departmentName: string | null
): boolean => {
  if (!departmentId && !departmentName) return true; // no filter — show everything
  // Match by department UUID if assigned
  if (departmentId) {
    if (order.department_id && order.department_id.toLowerCase() === departmentId.toLowerCase()) return true;
    if (order.department && order.department.toLowerCase() === departmentId.toLowerCase()) return true;
  }
  // Match by department name if assigned
  if (departmentName && order.department) {
    if (order.department.toLowerCase() === departmentName.toLowerCase()) return true;
  }
  return false;
};

// In-memory cache for waiter department assignment to avoid repetitive RPC roundtrips
interface CachedDepartment {
  departmentId: string | null;
  departmentName: string | null;
  timestamp: number;
}
const DEPARTMENT_CACHE_TTL_MS = 5 * 60 * 1000;
const departmentCache = new Map<string, CachedDepartment>();

export const useWaiterOrders = (venueId: string | null) => {
  const [orders, setOrders] = useState<WaiterActiveOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The waiter's assigned department (null = unassigned, see all orders)
  const [assignedDepartmentId, setAssignedDepartmentId] = useState<string | null>(null);
  const [assignedDepartmentName, setAssignedDepartmentName] = useState<string | null>(null);

  const prevReadyOrdersCount = useRef(0);
  const prevOrdersCount = useRef(0);
  const isInitialLoad = useRef(true);
  const lastFetchedAtRef = useRef<number>(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadOrders = useCallback(async () => {
    if (!venueId) return;
    try {
      setError(null);
      const data = await fetchActiveWaiterOrders(venueId);
      lastFetchedAtRef.current = Date.now();

      // Apply department filter if this waiter is assigned to one
      const filtered = data.filter(o =>
        orderMatchesDepartment(o, assignedDepartmentId, assignedDepartmentName)
      );

      setOrders(filtered);

      const readyCount = filtered.filter(o => o.pipeline_status === 'ready').length;

      // Play sound alerts on state changes (skip on first mount) using audio singletons
      if (!isInitialLoad.current) {
        if (readyCount > prevReadyOrdersCount.current) {
          playReadySound();
        } else if (filtered.length > prevOrdersCount.current) {
          playCallSound();
        }
      }

      prevReadyOrdersCount.current = readyCount;
      prevOrdersCount.current = filtered.length;
      isInitialLoad.current = false;
    } catch (err: any) {
      console.error('[useWaiterOrders] Failed to load orders:', err);
      setError(err?.message || 'Failed to load active orders');
    } finally {
      setIsLoading(false);
    }
  }, [venueId, assignedDepartmentId, assignedDepartmentName]);

  const loadOrdersRef = useRef(loadOrders);
  loadOrdersRef.current = loadOrders;

  // Schedules a debounced reload to coalesce rapid burst events into a single database query
  const scheduleRefresh = useCallback((immediate?: boolean) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (immediate) {
      loadOrdersRef.current();
      return;
    }
    debounceTimerRef.current = setTimeout(() => {
      loadOrdersRef.current();
    }, 300);
  }, []);

  // 1. One-time bootstrap for waiter department assignment using in-memory TTL cache
  useEffect(() => {
    if (!venueId) return;

    let isMounted = true;
    const cachedDept = departmentCache.get(venueId);
    if (cachedDept && Date.now() - cachedDept.timestamp < DEPARTMENT_CACHE_TTL_MS) {
      setAssignedDepartmentId(cachedDept.departmentId);
      setAssignedDepartmentName(cachedDept.departmentName);
      return;
    }

    const bootstrapDepartment = async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc('get_my_waiter_department', {
          p_venue_id: venueId,
        });
        if (!rpcError && data && isMounted) {
          const deptId = (data as any).department_id ?? null;
          const deptName = (data as any).department_name ?? null;
          departmentCache.set(venueId, {
            departmentId: deptId,
            departmentName: deptName,
            timestamp: Date.now(),
          });
          setAssignedDepartmentId(deptId);
          setAssignedDepartmentName(deptName);
        }
      } catch (err) {
        console.warn('[useWaiterOrders] Could not fetch department assignment:', err);
      }
    };

    bootstrapDepartment();

    return () => {
      isMounted = false;
    };
  }, [venueId]);

  // 2. Realtime subscription: event-driven synchronization with zero periodic polling
  useEffect(() => {
    if (!venueId) return;

    loadOrdersRef.current();

    // Subscribe to real-time events on orders and waiter requests
    const channel = supabase
      .channel(`waiter-orders-live-${venueId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `venue_id=eq.${venueId}`,
        },
        (payload) => {
          // Optimistically update order status and timestamps immediately to keep UI ultra-responsive
          if (payload.eventType === 'UPDATE' && payload.new?.id) {
            const updated = payload.new as any;
            setOrders(prev =>
              prev.map(o => {
                if (o.id !== updated.id) return o;
                let nextPipeline = o.pipeline_status;
                if (updated.status === 'pending') nextPipeline = 'new';
                else if (updated.status === 'preparing') nextPipeline = 'preparing';
                else if (updated.status === 'ready') nextPipeline = 'ready';
                else if (updated.status === 'completed') nextPipeline = 'done';

                return {
                  ...o,
                  pipeline_status: nextPipeline,
                  payment_status: updated.payment_status ?? o.payment_status,
                  confirmed_to_kitchen_at: updated.confirmed_to_kitchen_at ?? o.confirmed_to_kitchen_at,
                  is_confirmed_to_kitchen: Boolean(
                    updated.status === 'confirmed' || updated.confirmed_to_kitchen_at
                  ),
                };
              })
            );
          }
          scheduleRefresh();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'waiter_requests',
          filter: `venue_id=eq.${venueId}`,
        },
        () => {
          scheduleRefresh();
        }
      )
      .subscribe((status) => {
        // Reconnection recovery: if network was interrupted and channel resubscribes, catch up missed mutations
        if (status === 'SUBSCRIBED' && Date.now() - lastFetchedAtRef.current > 30000) {
          scheduleRefresh(true);
        }
      });

    // Reconnection catch-up: refresh only when device reconnects or tab becomes visible after idle (>60s)
    const handleReconnection = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetchedAtRef.current > 60000) {
        scheduleRefresh(true);
      }
    };

    window.addEventListener('visibilitychange', handleReconnection);
    window.addEventListener('online', handleReconnection);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      window.removeEventListener('visibilitychange', handleReconnection);
      window.removeEventListener('online', handleReconnection);
      supabase.removeChannel(channel);
    };
  }, [venueId, scheduleRefresh]);


  const handleConfirmToKitchen = async (orderId: string) => {
    setOrders(prev =>
      prev.map(o => (o.id === orderId ? { ...o, is_confirmed_to_kitchen: true } : o))
    );
    try {
      await confirmOrderToKitchen(orderId);
      loadOrders();
    } catch (err) {
      console.error('[useWaiterOrders] Confirm to kitchen failed:', err);
      loadOrders();
    }
  };

  const handleServeOrder = async (orderId: string) => {
    setOrders(prev =>
      prev.map(o => (o.id === orderId ? { ...o, pipeline_status: 'done' as const } : o))
    );
    try {
      await serveOrderToTable(orderId);
      loadOrders();
    } catch (err) {
      console.error('[useWaiterOrders] Serve order failed:', err);
      loadOrders();
    }
  };

  const handleCollectPayment = async (orderId: string, method: 'CASH' | 'CARD' = 'CASH') => {
    // For CARD: only mark payment as paid. The order remains open and the waiter
    // must still confirm serving. For CASH: close the order immediately.
    setOrders(prev =>
      prev.map(o => {
        if (o.id !== orderId) return o;
        if (method === 'CARD') {
          return { ...o, payment_status: 'paid' as const, payment_method: method };
        }
        return { ...o, payment_status: 'paid' as const, payment_method: method, pipeline_status: 'done' as const };
      })
    );
    try {
      await collectOrderPayment(orderId, method);
      loadOrders();
    } catch (err) {
      console.error('[useWaiterOrders] Collect payment failed:', err);
      loadOrders();
    }
  };

  const handleResolveGuestCall = async (requestId: string) => {
    try {
      await resolveGuestCall(requestId);
      loadOrders();
    } catch (err) {
      console.error('[useWaiterOrders] Resolve call failed:', err);
    }
  };

  const handleCancelOrder = async (orderId: string, reason?: string) => {
    try {
      await cancelOrder(orderId, reason);
      loadOrders();
    } catch (err) {
      console.error('[useWaiterOrders] Cancel order failed:', err);
      throw err;
    }
  };

  return {
    orders,
    isLoading,
    error,
    // Expose the department so the UI can show which department this waiter is scoped to
    assignedDepartmentName,
    refreshOrders: loadOrders,
    handleConfirmToKitchen,
    handleServeOrder,
    handleCollectPayment,
    handleResolveGuestCall,
    handleCancelOrder,
  };
};
